import React, { useEffect, useState } from 'react';
import {
  FiFileText,
  FiDollarSign,
  FiClipboard,
  FiUsers,
  FiCalendar,
  FiUploadCloud,
  FiTrendingUp,
  FiAlertCircle,
} from 'react-icons/fi';
import { loadTable } from '../../db-viewer/data/dataAccess';
import './UserDashboard.css';

interface DocRow {
  document_id?: number | string;
  filename?: string;
  created_at?: string;
  is_deleted?: number | string;
}

const emptyStyle: React.CSSProperties = { color: '#8a8a8a', fontSize: 14, padding: '8px 0', margin: 0 };

function timeAgo(iso?: string): string {
  if (!iso) return 'Recently uploaded';
  const then = new Date(String(iso).replace(' ', 'T')).getTime();
  if (isNaN(then)) return 'Recently uploaded';
  const diff = Date.now() - then;
  const day = 86400000;
  if (diff < 3600000) return `${Math.max(1, Math.round(diff / 60000))} min ago`;
  if (diff < day) return `${Math.round(diff / 3600000)} hours ago`;
  if (diff < 2 * day) return 'yesterday';
  return `${Math.round(diff / day)} days ago`;
}

const UserDashboard: React.FC = () => {
  const [docs, setDocs] = useState<DocRow[]>([]);

  useEffect(() => {
    let alive = true;
    loadTable('dms_documents')
      .then((rows: DocRow[]) => { if (alive) setDocs((rows || []).filter(d => d.is_deleted != 1)); })
      .catch(() => { if (alive) setDocs([]); });
    return () => { alive = false; };
  }, []);

  const docCount = docs.length;
  const recentDocs = [...docs]
    .sort((a, b) => String(b.created_at || '').localeCompare(String(a.created_at || '')))
    .slice(0, 3);

  return (
    <div className="user-dashboard">
      {/* Quick Stats */}
      <div className="stats-grid">
        <div className="stat-card">
          <div className="stat-icon blue"><FiFileText /></div>
          <div className="stat-info">
            <span className="stat-value">{docCount}</span>
            <span className="stat-label">Documents</span>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-icon green"><FiDollarSign /></div>
          <div className="stat-info">
            <span className="stat-value">$0</span>
            <span className="stat-label">Revenue (MTD)</span>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-icon orange"><FiClipboard /></div>
          <div className="stat-info">
            <span className="stat-value">0</span>
            <span className="stat-label">Pending Filings</span>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-icon purple"><FiCalendar /></div>
          <div className="stat-info">
            <span className="stat-value">0</span>
            <span className="stat-label">Upcoming Deadlines</span>
          </div>
        </div>
      </div>

      {/* Main Content Grid */}
      <div className="dashboard-grid">
        {/* Recent Documents */}
        <div className="dashboard-card">
          <div className="card-header">
            <h3><FiFileText /> Recent Documents</h3>
            <a href="/dashboard/dms" className="card-link">View All</a>
          </div>
          <div className="card-body">
            <div className="doc-list">
              {recentDocs.length === 0 ? (
                <p style={emptyStyle}>No documents yet. Upload your first document to get started.</p>
              ) : (
                recentDocs.map((d, i) => (
                  <div className="doc-item" key={d.document_id ?? i}>
                    <FiFileText className="doc-icon" />
                    <div className="doc-info">
                      <span className="doc-name">{d.filename || 'Untitled document'}</span>
                      <span className="doc-meta">{timeAgo(d.created_at)}</span>
                    </div>
                  </div>
                ))
              )}
            </div>
            <a href="/dashboard/dms" className="btn-upload" style={{ textDecoration: 'none' }}>
              <FiUploadCloud /> Upload Document
            </a>
          </div>
        </div>

        {/* Financial Overview */}
        <div className="dashboard-card">
          <div className="card-header">
            <h3><FiTrendingUp /> Financial Overview</h3>
            <a href="/dashboard/your-accounts" className="card-link">Details</a>
          </div>
          <div className="card-body">
            <div className="finance-row">
              <span>Income (this month)</span>
              <span className="amount positive">$0.00</span>
            </div>
            <div className="finance-row">
              <span>Expenses (this month)</span>
              <span className="amount negative">$0.00</span>
            </div>
            <div className="finance-row total">
              <span>Net Profit</span>
              <span className="amount positive">$0.00</span>
            </div>
            <p style={emptyStyle}>Record transactions in Your Accounts to see your financial overview.</p>
          </div>
        </div>

        {/* Upcoming Deadlines */}
        <div className="dashboard-card">
          <div className="card-header">
            <h3><FiAlertCircle /> Upcoming Deadlines</h3>
            <a href="/dashboard/calendar" className="card-link">Calendar</a>
          </div>
          <div className="card-body">
            <div className="deadline-list">
              <p style={emptyStyle}>No upcoming deadlines.</p>
            </div>
          </div>
        </div>

        {/* Quick Actions */}
        <div className="dashboard-card">
          <div className="card-header">
            <h3>Quick Actions</h3>
          </div>
          <div className="card-body">
            <div className="quick-actions">
              <a className="quick-action-btn" href="/dashboard/dms" style={{ textDecoration: 'none' }}>
                <FiUploadCloud />
                <span>Upload Document</span>
              </a>
              <a className="quick-action-btn" href="/dashboard/your-accounts" style={{ textDecoration: 'none' }}>
                <FiDollarSign />
                <span>Record Transaction</span>
              </a>
              <a className="quick-action-btn" href="/dashboard/tax-filing" style={{ textDecoration: 'none' }}>
                <FiClipboard />
                <span>Start Tax Filing</span>
              </a>
              <a className="quick-action-btn" href="/dashboard/contacts" style={{ textDecoration: 'none' }}>
                <FiUsers />
                <span>Add Contact</span>
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default UserDashboard;
